import { NumberField as NumberFieldPrimitive } from "@base-ui/react/number-field"
import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"

// A quantity stepper: a decrement, the current value, and an increment, sized
// for sitting at the end of a row rather than filling a form.
//
// Sizing is in px rather than rem because the app resets the root font size to
// 62.5% — `h-8` is 20px here, not the 32px the design calls for.
const VARIANTS = {
  solid: {
    group: "bg-blue-600 text-white",
    // bg-transparent is load-bearing: a bare <button> paints the browser's
    // native ButtonFace grey, which sits opaque on top of the group's blue
    // and never lets it show through since background-color doesn't inherit.
    button: "bg-transparent text-white hover:bg-blue-700 disabled:opacity-40",
    input: "bg-transparent text-white",
  },
  outlined: {
    group: "border border-blue-600 bg-white text-gray-900",
    button: "bg-transparent text-blue-600 hover:bg-blue-50 disabled:opacity-40",
    input: "bg-transparent text-gray-900",
  },
}

const NumberField = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  variant = "solid",
  ariaLabel,
  disabled = false,
  decrementLabel = "Decrease",
  incrementLabel = "Increase",
  decrementIcon = "fa-minus",
  incrementIcon = "fa-plus",
  onDecrementAtFloor,
}) => {
  const styles = VARIANTS[variant]

  // Base UI disables its own Decrement button once value <= min — correct
  // for a plain floor, but it means a click at the floor never reaches
  // onValueChange at all, so a caller relabeling the button "Remove" (see
  // the README) had no way to be told it was pressed. When onDecrementAtFloor
  // is given, let Base UI step one below the real floor so the button stays
  // enabled there, then intercept that one step below and report it as the
  // floor being reached instead of a real value change — the caller's value
  // never actually moves below min.
  const effectiveMin = onDecrementAtFloor && min != null ? min - step : min

  // Base UI's press-and-hold auto-repeat reuses the same value-below-min
  // condition on every tick, not just the first, so holding decrement from
  // well above the floor sails straight through it into onDecrementAtFloor.
  // What distinguishes "removing" from "holding past the floor" is whether
  // the press started already at the floor, so that's what's captured here
  // rather than trusting the value onValueChange happens to report.
  const startedAtFloorRef = React.useRef(false)

  return (
    <NumberFieldPrimitive.Root
      value={value}
      onValueChange={(next) => {
        // Base UI reports null when the input is cleared. A stepper has no
        // meaningful empty state, so leave the value where it was.
        if (next === null) return

        if (onDecrementAtFloor && min != null && next < min) {
          if (startedAtFloorRef.current) {
            onDecrementAtFloor()
          } else {
            onChange(min)
          }
          return
        }

        onChange(next)
      }}
      min={effectiveMin}
      max={max}
      step={step}
      disabled={disabled}
      data-design-system="NumberField"
    >
      <NumberFieldPrimitive.Group
        className={classNames(
          "inline-flex h-[32px] items-center rounded-full",
          styles.group
        )}
      >
        <NumberFieldPrimitive.Decrement
          aria-label={decrementLabel}
          onPointerDown={() => {
            startedAtFloorRef.current = min != null && value <= min
          }}
          className={classNames(
            "flex h-full w-[32px] items-center justify-center rounded-full",
            styles.button
          )}
        >
          <i className={classNames("fa-solid", decrementIcon)} />
        </NumberFieldPrimitive.Decrement>
        <NumberFieldPrimitive.Input
          aria-label={ariaLabel}
          className={classNames(
            "w-[32px] border-0 p-0 text-center text-sm font-semibold focus:outline-none",
            styles.input
          )}
        />
        <NumberFieldPrimitive.Increment
          aria-label={incrementLabel}
          className={classNames(
            "flex h-full w-[32px] items-center justify-center rounded-full",
            styles.button
          )}
        >
          <i className={classNames("fa-solid", incrementIcon)} />
        </NumberFieldPrimitive.Increment>
      </NumberFieldPrimitive.Group>
    </NumberFieldPrimitive.Root>
  )
}

NumberField.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  variant: PropTypes.oneOf(["solid", "outlined"]),
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  // At the floor, decrementing often means something other than "one less" —
  // removing the row entirely, say. The label and icon are the caller's so the
  // component carries no opinion about what the floor means.
  decrementLabel: PropTypes.string,
  incrementLabel: PropTypes.string,
  decrementIcon: PropTypes.string,
  incrementIcon: PropTypes.string,
  // Called instead of onChange when decrementing is attempted at min — the
  // button stays enabled there rather than disabling, so a caller relabeling
  // it "Remove" (via decrementLabel/decrementIcon) can actually act on the
  // click. Without this prop the decrement button disables at min as usual.
  onDecrementAtFloor: PropTypes.func,
}

export default NumberField
